import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Loader2, XCircle, Home } from "lucide-react";
import axios from "axios";

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL } from "@/utils/apiConfig";
import { useTranslation } from "@/hooks/useTranslation";

function PaymentSuccessPage() {
  const { token, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const { t: tKey } = useTranslation();  // COMMIT-5: setup (kart-guncelleme) mesaji icin i18n

  // Get current locale from URL path
  const locale = window.location.pathname.startsWith('/tr') ? 'tr' : 
                 window.location.pathname.startsWith('/ar') ? 'ar' :
                 window.location.pathname.startsWith('/de') ? 'de' :
                 window.location.pathname.startsWith('/fr') ? 'fr' :
                 window.location.pathname.startsWith('/es') ? 'es' : 'en';

  // Translations for PaymentSuccessPage
  const translations = {
    tr: {
      checkingPayment: "Ödeme durumunuz kontrol ediliyor...",
      pleaseWait: "Lütfen bekleyin",
      error: "Hata",
      backToPackages: "Paket Sayfasına Dön",
      paymentSuccess: "Ödeme Başarılı!",
      paymentFailed: "Ödeme Başarısız",
      paymentProcessing: "Ödeme İşleniyor",
      successMessage: "Paketiniz başarıyla yükseltildi! Yeni sorgu haklarınız hesabınıza eklendi.",
      redirecting: "Ana Sayfaya Yönlendiriliyorsunuz...",
      failedMessage: "Ödemeniz işlenemedi. Lütfen tekrar deneyin veya farklı bir ödeme yöntemi kullanın.",
      tryAgain: "Tekrar Dene",
      pendingMessage: "Ödemeniz hala işleniyor. Sonuç için lütfen profilinizi kontrol edin.",
      goToProfile: "Profile Git",
      noSessionId: "Ödeme bilgisi bulunamadı"
    },
    en: {
      checkingPayment: "Checking your payment status...",
      pleaseWait: "Please wait",
      error: "Error",
      backToPackages: "Back to Packages",
      paymentSuccess: "Payment Successful!",
      paymentFailed: "Payment Failed",
      paymentProcessing: "Processing Payment",
      successMessage: "Your package has been upgraded! New credits have been added to your account.",
      redirecting: "Redirecting to Homepage...",
      failedMessage: "Your payment could not be processed. Please try again or use a different payment method.",
      tryAgain: "Try Again",
      pendingMessage: "Your payment is still being processed. Please check your profile for results.",
      goToProfile: "Go to Profile",
      noSessionId: "Payment information not found"
    },
    de: {
      checkingPayment: "Zahlungsstatus wird überprüft...",
      pleaseWait: "Bitte warten",
      error: "Fehler",
      backToPackages: "Zurück zu Paketen",
      paymentSuccess: "Zahlung erfolgreich!",
      paymentFailed: "Zahlung fehlgeschlagen",
      paymentProcessing: "Zahlung wird verarbeitet",
      successMessage: "Ihr Paket wurde aktualisiert! Neue Guthaben wurden Ihrem Konto hinzugefügt.",
      redirecting: "Weiterleitung zur Startseite...",
      failedMessage: "Ihre Zahlung konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.",
      tryAgain: "Erneut versuchen",
      pendingMessage: "Ihre Zahlung wird noch verarbeitet. Bitte überprüfen Sie Ihr Profil.",
      goToProfile: "Zum Profil",
      noSessionId: "Zahlungsinformationen nicht gefunden"
    },
    fr: {
      checkingPayment: "Vérification du statut de paiement...",
      pleaseWait: "Veuillez patienter",
      error: "Erreur",
      backToPackages: "Retour aux forfaits",
      paymentSuccess: "Paiement réussi!",
      paymentFailed: "Paiement échoué",
      paymentProcessing: "Paiement en cours",
      successMessage: "Votre forfait a été mis à jour! De nouveaux crédits ont été ajoutés à votre compte.",
      redirecting: "Redirection vers la page d'accueil...",
      failedMessage: "Votre paiement n'a pas pu être traité. Veuillez réessayer.",
      tryAgain: "Réessayer",
      pendingMessage: "Votre paiement est encore en cours de traitement. Veuillez vérifier votre profil.",
      goToProfile: "Aller au profil",
      noSessionId: "Informations de paiement introuvables"
    },
    es: {
      checkingPayment: "Verificando el estado del pago...",
      pleaseWait: "Por favor espere",
      error: "Error",
      backToPackages: "Volver a paquetes",
      paymentSuccess: "¡Pago exitoso!",
      paymentFailed: "Pago fallido",
      paymentProcessing: "Procesando pago",
      successMessage: "¡Su paquete ha sido actualizado! Se han agregado nuevos créditos a su cuenta.",
      redirecting: "Redirigiendo a la página principal...",
      failedMessage: "No se pudo procesar su pago. Por favor intente de nuevo.",
      tryAgain: "Intentar de nuevo",
      pendingMessage: "Su pago aún se está procesando. Por favor revise su perfil.",
      goToProfile: "Ir al perfil",
      noSessionId: "Información de pago no encontrada"
    },
    ar: {
      checkingPayment: "جارٍ التحقق من حالة الدفع...",
      pleaseWait: "يرجى الانتظار",
      error: "خطأ",
      backToPackages: "العودة إلى الباقات",
      paymentSuccess: "تم الدفع بنجاح!",
      paymentFailed: "فشل الدفع",
      paymentProcessing: "جارٍ معالجة الدفع",
      successMessage: "تم ترقية باقتك! تمت إضافة أرصدة جديدة إلى حسابك.",
      redirecting: "جارٍ التحويل إلى الصفحة الرئيسية...",
      failedMessage: "لم يتم معالجة الدفع. يرجى المحاولة مرة أخرى.",
      tryAgain: "حاول مرة أخرى",
      pendingMessage: "لا يزال الدفع قيد المعالجة. يرجى مراجعة ملفك الشخصي.",
      goToProfile: "الذهاب إلى الملف الشخصي",
      noSessionId: "معلومات الدفع غير موجودة"
    }
  };
  
  const t = translations[locale] || translations.en;
  const isRTL = locale === 'ar';

  // Locale-aware paths. FIX: en-ozel '/' (dil-secim landing) KALDIRILDI -> secili-dilin APP'ine (/{locale}/) doner.
  const homePath = `/${locale}/`;
  const pricingPath = locale === 'tr' ? '/tr/kredi-paketleri' : `/${locale}/pricing`;
  const profilePath = locale === 'tr' ? '/tr/hesap-ayarlari' : `/${locale}/account-settings`;

  // Auto-redirect on success
  useEffect(() => {
    if (paymentStatus === "success") {
      const timer = setTimeout(() => {
        navigate(homePath);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus, navigate, homePath]);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      // Get session_id from URL (sent by Stripe success_url)
      const sessionId = searchParams.get("session_id");

      if (!sessionId) {
        setError(t.noSessionId);
        setLoading(false);
        return;
      }

      // Poll payment status (max 5 attempts, 2 seconds apart)
      let attempts = 0;
      const maxAttempts = 5;

      const pollStatus = async () => {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/payments/status/${sessionId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          const status = response.data.payment_status;

          // COMMIT-5: setup (kart-guncelleme) oturumu -> satin-alma DEGIL, kart guncelleme mesaji
          if (response.data.mode === "setup") {
            setPaymentStatus("setup");
            setLoading(false);
            await refreshUserData();
            return;
          }

          if (status === "paid") {
            setPaymentStatus("success");
            setLoading(false);
            // CRITICAL: Force refresh user data to show new credits immediately
            // Clear ALL cached user data before refresh
            localStorage.removeItem('user');
            localStorage.removeItem('user_cache_timestamp');
            await refreshUserData();
            return;
          } else if (status === "failed") {
            setPaymentStatus("failed");
            setLoading(false);
            return;
          }

          // If still pending and haven't reached max attempts, try again
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000);
          } else {
            setPaymentStatus("pending");
            setLoading(false);
          }
        } catch (err) {
          setError(err.response?.data?.detail || t.checkingPayment);
          setLoading(false);
        }
      };

      // Start polling
      pollStatus();
    };

    if (token) {
      checkPaymentStatus();
    }
  }, [token, searchParams, refreshUserData, t.noSessionId, t.checkingPayment]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="bg-gray-900 border-gray-800 max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-[#FFDD59] mx-auto mb-4" />
            <p className="text-white text-lg">{t.checkingPayment}</p>
            <p className="text-gray-400 text-sm mt-2">{t.pleaseWait}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="bg-gray-900 border-red-800 max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <XCircle className="w-6 h-6" />
              {t.error}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-white mb-4">{error}</p>
            <Link to={pricingPath}>
              <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold">
                {t.backToPackages}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
      <Card className={`bg-gray-900 ${
        (paymentStatus === "success" || paymentStatus === "setup") ? "border-green-500" :
        paymentStatus === "failed" ? "border-red-500" :
        "border-yellow-500"
      } max-w-md w-full mx-4`}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${
            (paymentStatus === "success" || paymentStatus === "setup") ? "text-green-400" :
            paymentStatus === "failed" ? "text-red-400" :
            "text-yellow-400"
          }`}>
            {paymentStatus === "success" && (
              <>
                <CheckCircle className="w-6 h-6" />
                {t.paymentSuccess}
              </>
            )}
            {paymentStatus === "failed" && (
              <>
                <XCircle className="w-6 h-6" />
                {t.paymentFailed}
              </>
            )}
            {paymentStatus === "pending" && (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                {t.paymentProcessing}
              </>
            )}
            {paymentStatus === "setup" && (
              <>
                <CheckCircle className="w-6 h-6" />
                {tKey("paymentmethod.title")}
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {paymentStatus === "success" && (
            <>
              <p className="text-white mb-4">
                {t.successMessage}
              </p>
              <p className="text-gray-400 text-sm flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t.redirecting}
              </p>
            </>
          )}
          {paymentStatus === "failed" && (
            <>
              <p className="text-white mb-4">
                {t.failedMessage}
              </p>
              <Link to={pricingPath}>
                <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold">
                  {t.tryAgain}
                </Button>
              </Link>
            </>
          )}
          {paymentStatus === "pending" && (
            <>
              <p className="text-white mb-4">
                {t.pendingMessage}
              </p>
              <Link to={profilePath}>
                <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold">
                  {t.goToProfile}
                </Button>
              </Link>
            </>
          )}
          {paymentStatus === "setup" && (
            <>
              <p className="text-white mb-4">
                {tKey("paymentmethod.success")}
              </p>
              <Link to={homePath}>
                <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold">
                  {t.goToProfile}
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PaymentSuccessPage;
